import {
  useFormatToCurrencyCutoff,
  useFormatToDecimal,
  useTranslation,
} from '@dreamstack/i18n'
import { Marker, Share } from '@dreamstack/icons'
import type { FullProject } from '@dreamstack/web/lib/prisma/projects'
import type { PropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import CloseIcon from '@material-ui/icons/Close'
import first from 'lodash/first'
import minBy from 'lodash/minBy'
import some from 'lodash/some'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'
import 'twin.macro'
import tw, { styled } from 'twin.macro'
import {
  Css_Text_20_30_To_16_24,
  Css_Text_32_44_To_16_24,
  HeadingH3,
  HeadingH4,
  PropAndProjectContactForm,
  ShareContactWrapper,
  SimpleMuiButton,
  blurText,
  useWebShareAuto,
} from '../index'
import { ProjectInformationList } from '../project/ProjectInformationList'
import { PropertyContactButton } from './PropertyContactButton'
import { scrollToMap } from './map/scrollToMap'

const StyledEur = styled.span(() => [
  tw`font-semibold`,
  Css_Text_32_44_To_16_24,
])

const StyledMapButtonText = styled.span(() => [
  tw`ml-px-12`,
  Css_Text_20_30_To_16_24,
])

export const StyledContainer = tw.div`col-span-12 px-px-24 pt-px-32 md:(px-px-40 pt-px-48) ml:(px-px-48 pt-px-64) lg:(col-span-8 col-start-1)`

const StyledPrice = tw.h2`text-px-24 leading-px-36 md:(text-px-40 leading-px-50) ml:(text-px-48 leading-px-50) font-semibold inline`
const StyledPriceText = tw.h2`text-px-16 leading-px-24 mr-px-8 md:(text-px-24 leading-px-36 mr-px-12) ml:(text-px-28 leading-px-42) inline font-semibold`
const PriceSubText = tw.h4`font-normal text-px-16 leading-px-24 pb-px-8 md:(pb-px-16) ml:(text-px-20 leading-px-30)`

export const ProjectInformation: FunctionComponent<
  React.PropsWithChildren<{
    properties?: PropertyPreview[]
    property?: PropertyPreview
    project?: FullProject
    statusLabel?: string
    isOnDarkBg?: boolean
  }>
> = ({ properties = [], property, project }) => {
  const formatCurrency = useFormatToCurrencyCutoff()
  const t = useTranslation()
  const formatDecimal = useFormatToDecimal()
  const { canShare, share } = useWebShareAuto()
  const [openContactForm, setOpenContactForm] = useState<boolean>(false)

  // fix begin
  // description https://github.com/vercel/next.js/discussions/17443
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  // fix end

  // TODO: Refactor.
  // Filter out properties with price_per_sqm under 1500, requested by accentro
  let propertiesWithPriceOver1500 = properties.filter(
    (p) => p?.price_per_sqm && p?.price_per_sqm > 1500
  )

  // If there are no properties with price_per_sqm over 1500, use all properties to avoid mistakes like 0€ price
  if (propertiesWithPriceOver1500.length === 0) {
    propertiesWithPriceOver1500 = properties
  }
  const safeProperty =
    property ||
    (minBy(propertiesWithPriceOver1500, (p) => p?.price) ??
      first(propertiesWithPriceOver1500))

  const hasPrice = !(safeProperty?.price == null)

  const price = hasPrice ? formatCurrency(safeProperty.price!) : '???.???'

  const price_per_sqm =
    hasPrice && safeProperty.price_per_sqm
      ? formatDecimal(safeProperty.price_per_sqm)
      : '???'

  const projectHasGeoLocation = some(properties, (p) => !!p.geoLocation)

  const showGotoMapButton = !!property?.geoLocation || projectHasGeoLocation

  const logoUrl = project?.logo_url

  return (
    <>
      <StyledContainer>
        <div tw="flex flex-col md:flex-row">
          {logoUrl && (
            <div tw="h-px-160 w-px-274 mb-px-32 md:mr-px-48 self-center relative">
              <Image
                tw="object-contain"
                layout="fill"
                src={logoUrl}
                alt="Project Logo"
              />
            </div>
          )}
          <div tw="flex justify-between">
            <div tw="flex flex-col flex-wrap">
              <HeadingH3>
                <div>
                  {safeProperty?.street + ' ' + safeProperty?.house_number}
                </div>
                <div>{safeProperty?.zip_code + ' ' + safeProperty?.city}</div>
              </HeadingH3>
              {showGotoMapButton && (
                <div tw="text-accentroAqua-full font-semibold py-px-4 mt-px-16 md:mt-px-20 lg:mt-px-24 cursor-pointer">
                  <div tw="flex justify-start items-center">
                    <Marker tw="h-px-32 w-px-32" />
                    <StyledMapButtonText onClick={scrollToMap}>
                      {t('accentro:showOnMap')}
                    </StyledMapButtonText>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <ProjectInformationList properties={properties} project={project} />
      </StyledContainer>
      <div tw="col-span-12 px-px-24 mt-px-40 md:(mt-px-32 px-px-40 flex justify-between pb-px-64) lg:(mt-px-80)">
        <div css={!hasPrice ? blurText : undefined} tw="relative">
          <StyledPriceText>{t('accentro:startingAt')}</StyledPriceText>
          <StyledPrice>
            {price} <StyledEur>EUR</StyledEur>
          </StyledPrice>
          <PriceSubText>
            {price_per_sqm} / {t('accentro:squareMeter')}
          </PriceSubText>
          <PriceSubText>{t('accentro:freeOfCommission')}</PriceSubText>
        </div>
        <ShareContactWrapper canShare={mounted && canShare} isProperty={false}>
          {/* TODO: Comment back in when print view is implemented */}
          {/* <StyledPrint isProject={isProject} tw="mr-px-12 (md:mx-px-12)" /> */}
          {mounted && canShare && (
            <Share
              tw="h-px-32 w-px-32 mt-px-24 cursor-pointer md:(mt-0)"
              onClick={() => share()}
            />
          )}
          {hasPrice && (
            <div tw="md:mt-px-32">
              {!!openContactForm ? (
                <div tw="flex flex-wrap my-px-32 md:(my-0)">
                  <SimpleMuiButton
                    variant="text"
                    tw="focus:outline-none cursor-pointer"
                    onClick={() => {
                      setOpenContactForm(false)
                    }}
                  >
                    <HeadingH4 tw="text-accentroWhite-full md:(px-px-16)">
                      {t('accentro:contactform.discardButton')}
                    </HeadingH4>
                    <CloseIcon tw="text-accentroWhite-full" />
                  </SimpleMuiButton>
                </div>
              ) : (
                <div>
                  <PropertyContactButton
                    onClick={() => {
                      setOpenContactForm(true)
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </ShareContactWrapper>
      </div>
      <div tw="col-span-12" css={!openContactForm ? tw`hidden` : undefined}>
        <PropAndProjectContactForm projectId={project?.id} isOnDarkBg={true} />
      </div>
    </>
  )
}
